import type { SanityImageSource } from "@sanity/image-url";

import { getAlt } from "@/lib/image";
import { sanityImageUrlWithDocumentVersion, urlForImage } from "@/sanity/lib/image";

/** Sanity image shape after GROQ (optional inline `alt` + Media Library `assetAlt`). */
export type CmsPageImage = {
  asset?: unknown;
  alt?: string | null;
  assetAlt?: string | null;
  /** Set when resolving from a document; used for `?v=` cache busting. */
  _sanityDocumentUpdatedAt?: string | null;
} | null;

export type SitePageImageMap = Record<string, CmsPageImage>;

const CMS_IMAGE_PLACEHOLDER_SRC = "/images/cms-image-placeholder.svg";
const CMS_IMAGE_PLACEHOLDER_ALT = "Image placeholder";

type SitePageDoc = {
  _updatedAt?: string;
  pageImages?: Array<{ key?: string; image?: CmsPageImage }>;
} | null;

export function buildSitePageImageMap(doc: SitePageDoc): SitePageImageMap {
  const map: SitePageImageMap = {};
  const documentUpdatedAt = doc?._updatedAt;
  for (const row of doc?.pageImages ?? []) {
    const k = row.key?.trim();
    if (k && row.image?.asset) {
      map[k] = {
        ...row.image,
        _sanityDocumentUpdatedAt: documentUpdatedAt,
      };
    }
  }
  return map;
}

/**
 * Resolves a Site page `pageImages` slot by key.
 * When the key has no published Sanity image, returns the shared placeholder (not local marketing assets).
 */
export function resolvePageImage(
  key: string,
  map: SitePageImageMap | undefined,
): { src: string; alt: string } {
  const img = map?.[key];
  if (img?.asset) {
    return {
      src: sanityImageUrlWithDocumentVersion(
        urlForImage(img as SanityImageSource).width(2000).quality(85).url(),
        img._sanityDocumentUpdatedAt,
      ),
      alt: getAlt(img),
    };
  }
  return {
    src: CMS_IMAGE_PLACEHOLDER_SRC,
    alt: CMS_IMAGE_PLACEHOLDER_ALT,
  };
}
